import {
  Component,
  DestroyRef,
  ViewChild,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

import { Filters } from 'src/app/components/lista-tabellare/classes/lista-tabellare-data-source';
import { SpinnerOverlayComponent } from 'src/app/components/spinner-overlay/spinner-overlay.component';
import {
  DomandeValidazioneIro,
  DoppiaMaterialitaService,
  QuestionarioValidazioneIro,
  SezioniValidazioneIro,
} from 'src/app/services/doppia-materialita/doppia-materialita.service';
import { Domande } from 'src/app/services/informativa-aziendale/informativa-aziendale.service';
import {
  UnitaDiMisura,
  UnitaDiMisuraService,
} from 'src/app/services/unita-di-misura/unita-di-misura.service';
import { UtilityService } from 'src/app/services/utility/utility.service';

@Component({
  selector: 'app-anteprima-validazione-iro',
  standalone: false,
  templateUrl: './anteprima-validazione-iro.component.html',
  styleUrl: './anteprima-validazione-iro.component.scss',
})
export class AnteprimaValidazioneIroComponent {
  // ─── Injections ────────────────────────────────────────────────────────────
  private readonly sanitizer = inject(DomSanitizer);
  private readonly doppiaMaterialitaService = inject(DoppiaMaterialitaService);
  private readonly unitaDiMisuraService = inject(UnitaDiMisuraService);
  private readonly utilityService = inject(UtilityService);
  private readonly destroyRef = inject(DestroyRef);

  // ─── ViewChild ─────────────────────────────────────────────────────────────
  @ViewChild(SpinnerOverlayComponent) spinnerOver!: SpinnerOverlayComponent;

  // ─── Signal Inputs ─────────────────────────────────────────────────────────
  readonly questionarioValidazione = input.required<QuestionarioValidazioneIro>();
  readonly temporaryToken = input<string | undefined>(undefined);
  readonly isCompEsterna = input<boolean>(false);
  readonly readOnly = input<boolean>(false);
  readonly anteprima = input<boolean>(false);
  readonly landingPageRaw = input<string | undefined>(undefined, { alias: 'landingPage' });

  // ─── Signal Outputs ────────────────────────────────────────────────────────
  readonly completato = output<{ questionario: QuestionarioValidazioneIro; isBozza: boolean }>();

  // ─── State Signals ─────────────────────────────────────────────────────────
  readonly inizializzato = signal(false);
  readonly arrayUm = signal<Record<string, UnitaDiMisura[]>>({});

  // ─── Computed ──────────────────────────────────────────────────────────────
  readonly landingPage = computed<SafeHtml>(() =>
    this.sanitizer.bypassSecurityTrustHtml(this.landingPageRaw() ?? ''),
  );

  readonly sezioni = computed<SezioniValidazioneIro[]>(
    () => this.questionarioValidazione()?.sezioni ?? [],
  );

  // ─── Effects ───────────────────────────────────────────────────────────────
  constructor() {
    effect(() => {
      const questionario = this.questionarioValidazione();
      if (!questionario) return;

      // Calcola la struttura (sincrono, non richiede il DOM)
      this._calcolaStrutturaSottodomande(questionario);

      // Carica le unità di misura dopo che la view è pronta
      setTimeout(() => this._inizializzaParametri(questionario), 0);
    });
  }

  // ─── Event Handlers ────────────────────────────────────────────────────────

  onRadioChange(event: any, idxSez: number, idxDom: number): void {
    // Filtra gli eventi non provenienti da un radio-group
    if (!event?.source?.radioGroup) return;
    this.sezioni()[idxSez].domande[idxDom].risposte = [event.value];
  }

  cambioCheck(event: any, idxAmb: number, idxTem: number, chiave: string): void {
    const domanda = this.sezioni()[idxAmb].domande[idxTem];

    if (event.checked) {
      domanda.risposte ??= [];
      domanda.risposte.push(chiave);
    } else {
      const idx = domanda.risposte?.findIndex((r) => r === chiave) ?? -1;
      if (idx > -1) {
        domanda.risposte!.splice(idx, 1);

        // Rimuove anche eventuali risposte associate alle opzioni con valore
        const opzione = domanda.opzioniConValore?.find((opz) => opz.chiave === chiave);
        if (opzione) {
          opzione.risposta = undefined;
          opzione.value = undefined;
        }
      }
    }
  }

  checkSotto(risposte: string[] | undefined, chiave: string): boolean {
    return risposte?.some((r) => r === chiave) ?? false;
  }

  salva(): void {
    if (!this._validaQuestionario()) {
      this.utilityService.openSnackBar(
        'Rispondi a tutte le domande per salvare la compilazione.',
        '',
        { duration: 2000, panelClass: ['red-snackbar'] },
      );
      return;
    }

    // Clona in profondità per non alterare lo stato originale
    const compilato: QuestionarioValidazioneIro = structuredClone(this.questionarioValidazione());

    compilato.sezioni.forEach((sez: SezioniValidazioneIro) => {
      sez.domande.forEach((dom) => {
        delete dom.valid;

        (dom.opzioni ?? []).forEach((opz) => {
          delete opz.objSottodomande;
          delete opz.valid;
        });

        if (dom.metadati) {
          (['impatti', 'rischi', 'opportunita'] as const).forEach((key) =>
            (dom.metadati[key] ?? []).forEach((val: any) => delete val.stringaDatiIro),
          );
        }
      });
    });

    delete compilato.id;
    this.completato.emit({ questionario: compilato, isBozza: false });
  }

  // ─── Private: Validazione ──────────────────────────────────────────────────

  private _validaQuestionario(): boolean {
    const sottoDomandeAbilitate: Record<string, boolean> = {};

    return this.questionarioValidazione()
      ?.sezioni.flatMap((sez) => sez.domande)
      .map((domanda): boolean => {
        // Sottodomanda non abilitata → considerata valida (non richiesta)
        if (domanda.sottodomanda && !sottoDomandeAbilitate[domanda.id]) {
          return true;
        }

        switch (domanda.tipo) {
          case 'scelta_singola':
          case 'scelta_multipla': {
            if (domanda.risposte?.length > 0) {
              domanda.risposte.forEach((risp: string) => {
                domanda.opzioni
                  .find((opz) => opz.chiave === risp)
                  ?.sottodomande?.forEach((id) => (sottoDomandeAbilitate[id] = true));
              });
              domanda.valid = true;
              return true;
            }
            domanda.valid = false;
            return false;
          }

          case 'scelta_multipla_valore': {
            if (!(domanda.risposte?.length > 0)) {
              domanda.valid = false;
              return false;
            }
            return (domanda.opzioniConValore ?? [])
              .map((opz) => {
                const selezionata = domanda.risposte?.includes(opz.chiave);
                const valida = !selezionata || opz.value || opz.risposta;
                if (selezionata && !opz.value && !opz.risposta) opz.valid = false;
                domanda.valid = !!valida;
                return !!valida;
              })
              .every(Boolean);
          }

          case 'testo':
            return domanda.risposta !== null;

          case 'numero_intero':
          case 'numero_decimale': {
            if (!domanda.risposta || typeof domanda.risposta === 'string') return false;
            return (
              domanda.risposta.valore !== null &&
              domanda.risposta.valore !== undefined &&
              !!domanda.risposta.um
            );
          }

          case 'data':
            return !!domanda.data;

          default:
            console.error('[AnteprimaValidazioneIro] Tipo domanda non riconosciuto:', domanda.tipo);
            return false;
        }
      })
      .every(Boolean);
  }

  // ─── Private: Inizializzazione ─────────────────────────────────────────────

  private _inizializzaParametri(questionario: QuestionarioValidazioneIro): void {
    questionario.sezioni.forEach((sez) => {
      sez.domande.forEach((dom: Domande) => {
        if (dom.tipo !== 'numero_decimale' && dom.tipo !== 'numero_intero') return;

        dom.risposta ??= { valore: undefined, um: undefined };

        if (!dom.idCategoriaUnitaDiMisura) return;

        // Inizializza il bucket dell'UM
        this.arrayUm.update((current) => ({
          ...current,
          [dom.idCategoriaUnitaDiMisura]: [],
        }));

        const filters: Filters[] = [
          {
            chiave: 'idCategoriaUnitaDiMisura',
            operator: 'eq',
            values: [dom.idCategoriaUnitaDiMisura],
          },
        ];

        const token = this.temporaryToken();
        const obs$ = token
          ? this.unitaDiMisuraService.getUnitaDiMisuraConTempToken(token, 0, 1000, '', filters)
          : this.unitaDiMisuraService.getUnitaDiMisura(0, 1000, '', filters);

        obs$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((um) => {
          this.arrayUm.update((current) => ({
            ...current,
            [dom.idCategoriaUnitaDiMisura]: um?.content ?? [],
          }));
        });
      });
    });

    this.inizializzato.set(true);
  }

  // ─── Private: Struttura Sottodomande ──────────────────────────────────────

  private _calcolaStrutturaSottodomande(questionario: QuestionarioValidazioneIro): void {
    let lastIdTematica: string | undefined;

    (questionario.sezioni ?? []).forEach((sezione: SezioniValidazioneIro) => {
      (sezione.domande ?? []).forEach((domanda, index) => {
        if (!domanda.sottodomanda) {
          (domanda as DomandeValidazioneIro).idTematica = (domanda.metadati['tematica'] ??
            [])[0]?.id;

          const idTematicaCorrente = (domanda as DomandeValidazioneIro).idTematica;
          if (index === 0 || lastIdTematica !== idTematicaCorrente) {
            (domanda as DomandeValidazioneIro).infoTematica =
              domanda.metadati['tematica'][0].domanda;
          }

          lastIdTematica = idTematicaCorrente;
        }

        this._costruisciSottodomande(domanda, sezione.domande);
      });
    });
  }

  private _costruisciSottodomande(
    domanda: DomandeValidazioneIro,
    allDomande: DomandeValidazioneIro[],
  ): void {
    if (domanda.tipo !== 'scelta_singola' && domanda.tipo !== 'scelta_multipla') return;

    const metadatiMap: Record<string, (val: any) => string> = {
      impatti: (v) => this.doppiaMaterialitaService.calcolaStringaInfoImpattoIro(v),
      rischi: (v) => this.doppiaMaterialitaService.calcolaStringaInfoRischio(v),
      opportunita: (v) => this.doppiaMaterialitaService.calcolaStringaInfoOpportunita(v),
    };

    Object.entries(metadatiMap).forEach(([key, fn]) =>
      (domanda.metadati[key] ?? []).forEach((val: any) => (val.stringaDatiIro = fn(val))),
    );

    (domanda.opzioni ?? []).forEach((opzione) => {
      if (!opzione.sottodomande?.length) return;

      opzione.objSottodomande = opzione.sottodomande
        .map((idSottodomanda) => {
          const idx = allDomande.findIndex((d) => d.id === idSottodomanda);
          if (idx === -1) return null;

          this._costruisciSottodomande(allDomande[idx], allDomande);

          // We return the object with the extra property
          return Object.assign(allDomande[idx], { idxOriginale: idx });
        })
        // Highlighting the fix here: 'd is ...' is the magic spell
        .filter((d): d is DomandeValidazioneIro & { idxOriginale: number } => d !== null);
    });
  }

  /**
   * Calcolo l'indice da mostrare a video, essendo che è devo calcolare solo quelli di tipo scelta_singola o scelta_multipla e non quello della scelta_multipla_valore
   * @param currentIdx
   * @param sezione
   * @returns
   */
  getDisplayIndex(currentIdx: number, sezione: SezioniValidazioneIro): number {
    let counter = 0;
    for (let i = 0; i <= currentIdx; i++) {
      const dom = sezione.domande[i];
      if (!dom.sottodomanda && (dom.tipo === 'scelta_singola' || dom.tipo === 'scelta_multipla')) {
        counter++;
      }
    }
    return counter;
  }
}
